import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../api/client';

interface ConversationSummary {
  id: string;
  persona_id: string | null;
  title: string | null;
  koji_codename: string | null;
  started_at: number;
  last_turn_at: number;
  turn_count: number;
  status: string | null;
}

interface ListResponse {
  conversations: ConversationSummary[];
}

const COLLAPSED_STORAGE_KEY = 'synth-web-convo-sidebar-collapsed';

function readInitialCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function fmtRelative(unixSeconds: number): string {
  if (!unixSeconds) return '';
  const now = Math.floor(Date.now() / 1000);
  const diff = Math.max(0, now - unixSeconds);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

interface Props {
  activeConversationId: string | null;
  onPick: (conversationId: string) => void;
  /** Incremented by the parent to force a reload (e.g. after sending a new
   *  message that created a new conversation). */
  reloadKey?: number;
}

/**
 * Stage 12 (C1): persistent sidebar listing recent conversations on the
 * chat view. Clicking a row delegates to the parent's `onPick` which in
 * turn calls `useChat.hydrateConversation(id)` from Stage 11b.
 *
 * Collapsed state is persisted in localStorage.
 */
export function ConversationSidebar({
  activeConversationId,
  onPick,
  reloadKey,
}: Props): JSX.Element {
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readInitialCollapsed(),
  );

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await apiGet<ListResponse>('/api/conversations');
      setItems(res.conversations ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  const toggleCollapsed = (): void => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  if (collapsed) {
    return (
      <aside className="convo-sidebar convo-sidebar-collapsed">
        <button
          type="button"
          className="convo-sidebar-toggle"
          onClick={toggleCollapsed}
          title="Show conversations"
          aria-label="Show conversations"
        >
          ›
        </button>
      </aside>
    );
  }

  return (
    <aside className="convo-sidebar">
      <header className="convo-sidebar-header">
        <span className="convo-sidebar-title">Conversations</span>
        <button
          type="button"
          className="convo-sidebar-reload"
          onClick={() => void load()}
          title="Refresh list"
          aria-label="Refresh conversation list"
        >
          ↻
        </button>
        <button
          type="button"
          className="convo-sidebar-toggle"
          onClick={toggleCollapsed}
          title="Collapse"
          aria-label="Collapse conversations sidebar"
        >
          ‹
        </button>
      </header>
      {err ? (
        <div className="status-missing small">Error: {err}</div>
      ) : items === null ? (
        <div className="muted small">Loading…</div>
      ) : items.length === 0 ? (
        <div className="muted small convo-sidebar-empty">
          No conversations yet. Send a message to start one.
        </div>
      ) : (
        <ul className="convo-list" role="list">
          {items.map((c) => {
            const isActive = c.id === activeConversationId;
            const title =
              (c.title && c.title.trim()) ||
              c.koji_codename ||
              c.id.slice(0, 8);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className={
                    'convo-item' + (isActive ? ' convo-item-active' : '')
                  }
                  onClick={() => onPick(c.id)}
                  aria-pressed={isActive}
                  title={title}
                >
                  <div className="convo-item-title">{title}</div>
                  <div className="convo-item-meta muted small">
                    {c.turn_count ?? 0} turn{c.turn_count === 1 ? '' : 's'} ·{' '}
                    {fmtRelative(c.last_turn_at)}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
